import { ChevronDown, Check } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Button } from "@themes/pollinator/ui/Button";
import { Dropdown } from "@themes/pollinator/ui/Dropdown";
import { Type } from "@themes/pollinator/ui/Type";
import type { ChatAvailableCircle } from "@core/features/chat";

export type Props = {
  /** All circles the user can chat with in the active program. */
  availableCircles: ChatAvailableCircle[];
  /** The currently selected circle's id. */
  currentCircleId: number;
  /** Fired with the new circle's id when the user picks a different row. */
  onSelect: (circleId: number) => void;
};

/**
 * Compact "you can change" affordance for the chat sheet header. Renders
 * the current circle's name as a button; clicking opens a dropdown of
 * the user's other chat-eligible circles. Picking one closes the menu
 * and the conversation surface re-renders against the new selection.
 *
 * Only meaningful when the user has more than one circle to choose from
 * — the parent surface should not render this for single-circle users.
 */
export function ChatCircleSwitcher({
  availableCircles,
  currentCircleId,
  onSelect,
}: Props) {
  const current = availableCircles.find((c) => c.circle_id === currentCircleId);

  // Defensive: if the parent passed a stale id, surface the missing
  // selection rather than rendering an empty button. The resolver
  // guards against this case in production.
  const triggerLabel = current?.name ?? "Select a circle";

  return (
    <Dropdown
      data-testid="chat-circle-switcher"
      placement="bottom-start"
      triggerClassName="w-fit"
      trigger={
        <Button
          variant="link"
          iconComponent={ChevronDown}
          iconPosition="right"
          className="px-0 text-left"
        >
          {triggerLabel}
        </Button>
      }
    >
      <ul
        className="flex flex-col gap-1 min-w-48 py-1"
        role="list"
        data-testid="chat-circle-switcher-menu"
      >
        {availableCircles.map((circle) => {
          const isCurrent = circle.circle_id === currentCircleId;
          return (
            <li key={circle.circle_id}>
              <button
                type="button"
                onClick={() => {
                  if (!isCurrent) {
                    onSelect(circle.circle_id);
                  }
                }}
                aria-current={isCurrent ? "true" : undefined}
                className={twMerge(
                  "w-full flex flex-row items-center gap-2 px-3 py-2 rounded text-left",
                  isCurrent
                    ? "bg-brand-100 text-brand-900"
                    : "hover:bg-brand-50 text-brand-800"
                )}
                data-testid={`chat-circle-switcher-option-${circle.circle_id}`}
              >
                <span className="flex-1">
                  <Type variant="body2">{circle.name}</Type>
                </span>
                {isCurrent && (
                  <Check className="w-4 h-4 text-brand-700" aria-hidden="true" />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </Dropdown>
  );
}

export default ChatCircleSwitcher;
